import {alpha, createTheme, getContrastRatio, Theme} from "@mui/material/styles";
import {
    SurveyCornerStyle,
    SurveyQuestionStyle,
    SurveyTheme
} from "crqlar-react-common/dist/models/survey/survey.model";
import {surveyFontFamily} from "./surveyFont";
import {
    compositeSurveyColor,
    surveyContrastColor,
    SurveyControlColors,
    SurveyQuestionLayout,
    SurveyTextSets
} from "./surveyRunnerColors";

const CONTROL_HEIGHT_PADDING = '11.5px';

const CORNER_RADIUS: Record<SurveyCornerStyle, number> = {
    [SurveyCornerStyle.SQUARE]: 0,
    [SurveyCornerStyle.SOFT]: 6,
    [SurveyCornerStyle.ROUNDED]: 14,
};

const RUNNER_DEFAULTS = {
    primary: '#2F6FED',
    secondary: '#4FD1C5',
    background: '#F7F8FA',
    surface: '#FFFFFF',
    text: '#1A202C',
    textSecondary: '#5A6472',
    borderRadius: CORNER_RADIUS[SurveyCornerStyle.SOFT],
};

const solidColors = (surveyTheme: SurveyTheme | undefined) => {
    const pageColor = compositeSurveyColor(surveyTheme?.backgroundColor || RUNNER_DEFAULTS.background, '#FFFFFF');
    const surfaceColor = compositeSurveyColor(surveyTheme?.surfaceColor || RUNNER_DEFAULTS.surface, pageColor);
    const dark = getContrastRatio('#FFFFFF', surfaceColor) > getContrastRatio('#000000', surfaceColor);
    return {pageColor, surfaceColor, dark};
};

/*
    A text colour that reads (4.5:1) stays as chosen. One that does not is lifted to `target`: 7:1 for main text, so
    it stays clearly stronger than the secondary text that is lifted to the 4.5:1 minimum.
 */
const readable = (color: string, background: string, target: number) =>
    getContrastRatio(compositeSurveyColor(color, background), background) >= 4.5
        ? color
        : surveyContrastColor(color, [background], target);

/**
 * The text colours guests read on the surface. A chosen colour that does not read there is moved (see `readable`),
 * as the control borders are; `adjusted` tells the Design tab which ones it changed.
 */
export const surveyTextColors = (surveyTheme: SurveyTheme | undefined) => {
    const {surfaceColor, dark} = solidColors(surveyTheme);
    const chosenText = surveyTheme?.textColor;
    const chosenSecondary = surveyTheme?.textSecondaryColor;
    const text = chosenText
        ? readable(chosenText, surfaceColor, 7)
        : (dark ? '#FFFFFF' : RUNNER_DEFAULTS.text);
    const textSecondary = chosenSecondary
        ? readable(chosenSecondary, surfaceColor, 4.5)
        : (dark ? alpha(text, 0.75) : RUNNER_DEFAULTS.textSecondary);

    return {
        text,
        textSecondary,
        adjusted: {
            textColor: !!chosenText && text !== chosenText,
            textSecondaryColor: !!chosenSecondary && textSecondary !== chosenSecondary,
        },
    };
};

export const surveyThemeToMuiTheme = (surveyTheme: SurveyTheme | undefined): Theme => {
    const background = surveyTheme?.backgroundColor || RUNNER_DEFAULTS.background;
    const surface = surveyTheme?.surfaceColor || RUNNER_DEFAULTS.surface;
    const {pageColor, surfaceColor, dark} = solidColors(surveyTheme);
    const backgrounds = [surfaceColor, pageColor];
    const {text, textSecondary} = surveyTextColors(surveyTheme);
    const primary = surveyTheme?.primaryColor || RUNNER_DEFAULTS.primary;
    const secondary = surveyTheme?.secondaryColor || RUNNER_DEFAULTS.secondary;
    const foregroundOn = (fill: string) => {
        const resolved = backgrounds.map(color => compositeSurveyColor(fill, color));
        const contrast = (color: string) => Math.min(...resolved.map(backgroundColor => getContrastRatio(color, backgroundColor)));
        return contrast('#FFFFFF') > contrast('#121212') ? '#FFFFFF' : '#121212';
    };
    const controls: SurveyControlColors = {
        border: surveyContrastColor(alpha(text, 0.3), backgrounds, 3.05),
        hoverBorder: surveyContrastColor(alpha(text, 0.6), backgrounds, 3.5),
        accent: surveyContrastColor(primary, backgrounds, 4.55),
        focus: surveyContrastColor(primary, backgrounds, 3.1),
    };
    /*
        "Dark" above stays the surface's decision, and so do the text colours. Plain therefore puts questions
        straight on the page only where that text reads there; anywhere else they share one sheet in the
        surface colour, where it reads as it does on a card.
    */
    const questionLayout: SurveyQuestionLayout = {
        plain: surveyTheme?.questionStyle === SurveyQuestionStyle.PLAIN,
        textOnPage: !surveyTheme?.backgroundMedia?.mediaURI
            && getContrastRatio(compositeSurveyColor(text, pageColor), pageColor) >= 4.5,
    };
    // Outside the cards (footer, screens, headings) text stands on the page: SurveyTextOn switches to these.
    const surveyText: SurveyTextSets = {
        surface: {primary: text, secondary: textSecondary, link: surveyContrastColor(primary, [surfaceColor], 4.55)},
        page: {
            primary: readable(text, pageColor, 7),
            secondary: readable(textSecondary, pageColor, 4.5),
            link: surveyContrastColor(primary, [pageColor], 4.55),
        },
    };
    /*
        A filled button whose colour barely differs from what is behind it (navy on a dark page) keeps its colour
        and gets a light edge, so its shape still shows. Only where it fails 3:1, and measured where it fails.
    */
    const buttonEdge = (fill: string) => {
        const failing = backgrounds.filter(color => getContrastRatio(compositeSurveyColor(fill, color), color) < 3);
        if (!failing.length) return undefined;
        const edge = surveyContrastColor(fill, failing, 3);
        const shadow = `inset 0 0 0 1px ${edge}`;
        return {boxShadow: shadow, '&:hover, &:active': {boxShadow: shadow}};
    };
    const error = surveyContrastColor(dark ? '#EF5350' : '#D32F2F', backgrounds, 4.55);
    const focusSx = {outline: `2px solid ${controls.focus}`, outlineOffset: 3};

    return createTheme({
        surveyControls: controls,
        surveyQuestionLayout: questionLayout,
        surveyText,
        palette: {
            mode: dark ? 'dark' : 'light',
            primary: {main: primary, contrastText: foregroundOn(primary)},
            secondary: {main: secondary, contrastText: foregroundOn(secondary)},
            error: {main: error},
            background: {default: background, paper: surface},
            text: {primary: text, secondary: textSecondary, disabled: alpha(text, 0.45)},
            divider: alpha(text, 0.16),
            action: {
                active: surveyContrastColor(textSecondary, backgrounds, 3.05),
                hover: alpha(text, 0.06),
                selected: alpha(primary, 0.12),
                focus: alpha(text, 0.12),
                disabled: alpha(text, 0.45),
                disabledBackground: alpha(text, 0.12),
            },
        },
        typography: {
            fontFamily: surveyFontFamily(surveyTheme),
            h4: {fontSize: 30, fontWeight: 700},
            h5: {fontSize: 26, fontWeight: 700},
            h6: {fontSize: 24, fontWeight: 600},
            body1: {fontSize: 16},
            body2: {fontSize: 14},
            caption: {fontSize: 14},
            button: {fontSize: 16, textTransform: 'none'},
        },
        shape: {
            borderRadius: surveyTheme?.cornerStyle !== undefined && surveyTheme?.cornerStyle !== null
                ? CORNER_RADIUS[surveyTheme.cornerStyle]
                : RUNNER_DEFAULTS.borderRadius,
        },
        components: {
            MuiPaper: {
                styleOverrides: {root: {backgroundImage: 'none', colorScheme: dark ? 'dark' : 'light'}},
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    notchedOutline: {borderColor: controls.border},
                    input: {
                        paddingTop: CONTROL_HEIGHT_PADDING,
                        paddingBottom: CONTROL_HEIGHT_PADDING,
                        '&::placeholder': {color: textSecondary, opacity: 1},
                        '&.MuiInputBase-inputMultiline': {paddingTop: 0, paddingBottom: 0},
                        '&:-webkit-autofill': {
                            WebkitBoxShadow: `0 0 0 100px ${surfaceColor} inset`,
                            WebkitTextFillColor: text,
                            caretColor: text,
                        },
                    },
                    root: {
                        '&:hover .MuiOutlinedInput-notchedOutline': {borderColor: controls.hoverBorder},
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {borderColor: controls.focus, borderWidth: 2},
                        '&.Mui-error .MuiOutlinedInput-notchedOutline': {borderColor: error},
                        '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {borderColor: alpha(text, 0.2)},
                        '&.MuiInputBase-multiline': {
                            paddingTop: CONTROL_HEIGHT_PADDING,
                            paddingBottom: CONTROL_HEIGHT_PADDING,
                        },
                    },
                },
            },
            MuiButton: {
                styleOverrides: {
                    root: ({theme, ownerState}) => {
                        const paletteColor = ownerState.color && ownerState.color !== 'inherit'
                            ? theme.palette[ownerState.color]
                            : undefined;
                        const buttonAccent = paletteColor
                            ? surveyContrastColor(paletteColor.main, backgrounds, 4.55)
                            : controls.accent;
                        return {
                            '&.Mui-focusVisible': focusSx,
                            ...((ownerState.variant === 'outlined' || ownerState.variant === 'text') && {
                                color: buttonAccent,
                                ...(ownerState.variant === 'outlined' && {borderColor: controls.border}),
                                '&:hover': {
                                    borderColor: controls.hoverBorder,
                                    backgroundColor: alpha(buttonAccent, 0.08),
                                },
                            }),
                            ...(ownerState.variant === 'contained' && paletteColor && buttonEdge(paletteColor.main)),
                            ...(ownerState.variant === 'contained' && paletteColor && {
                                '&:hover': {
                                    backgroundColor: paletteColor.dark,
                                    color: foregroundOn(paletteColor.dark),
                                },
                            }),
                            '&.Mui-disabled': {
                                color: theme.palette.action.disabled,
                                ...(ownerState.variant === 'contained' && {backgroundColor: theme.palette.action.disabledBackground}),
                                ...(ownerState.variant === 'outlined' && {borderColor: alpha(text, 0.2)}),
                            },
                        };
                    },
                },
            },
            MuiIconButton: {
                styleOverrides: {root: {'&.Mui-focusVisible': focusSx}},
            },
            MuiCheckbox: {
                styleOverrides: {
                    root: {
                        color: controls.border,
                        '&.Mui-checked, &.MuiCheckbox-indeterminate': {color: controls.accent},
                        '&.Mui-focusVisible': focusSx,
                        '&.Mui-disabled': {color: alpha(text, 0.45)},
                    },
                },
            },
            MuiMenu: {
                styleOverrides: {paper: {border: `1px solid ${controls.border}`}},
            },
            MuiMenuItem: {
                styleOverrides: {
                    root: {
                        '&.Mui-selected': {backgroundColor: alpha(primary, 0.12)},
                        '&.Mui-selected:hover': {backgroundColor: alpha(primary, 0.18)},
                        '&.Mui-focusVisible': {...focusSx, outlineOffset: -2},
                    },
                },
            },
            MuiLinearProgress: {
                styleOverrides: {
                    root: {backgroundColor: alpha(text, 0.12)},
                    bar: {backgroundColor: controls.focus},
                },
            },
        },
    });
};
